import Logo from "@/ui/components/Logo";
import MessageList from "@/ui/components/MessageList";
import MessageInput from "@/ui/components/MessageInput";
import EmailPhoneInputWrapper from "@/ui/components/EmailPhoneInputWrapper";
import OtpInput from "@/ui/components/OtpInput";
import ButtonRow from "@/ui/components/ButtonRow";
import SkuOptions from "@/ui/components/SkuOptions";
import AppStoreTiles from "@/ui/components/AppStoreTiles";
import HamburgerMenu from "@/ui/components/HamburgerMenu";
import ActivityProgressBar from "@/ui/components/ActivityProgressBar";
import ActivityPicker from "@/ui/components/ActivityPicker";
import ThinkingAnimation from "@/ui/components/ThinkingAnimation";
import ActivityReportView from "@/ui/pages/activity_report/activityReportView";
import AppDownloadModal from "@/ui/components/AppDownloadModal";
import PrivacyPolicy from "@/ui/components/PrivacyPolicy";
import ContinueInAppModal from "@/ui/components/ContinueInApp";
import ChatScaffold from "@/ui/components/ChatScaffold";
import { pollForScore } from "@/lib/score_polling";
import { postChat, postEvent, postError, postLog } from "@/lib/api_controller";
import { FOOTER_TYPE } from "@/lib/enums";
import { useState, useRef, useEffect, useMemo } from "preact/hooks";
import { getQueryParamsFromUrl, isDevHost } from "@/lib/web";
import { getL11nString, uiLangIsRtl } from "@/l11n/l11n";
import { TaaliaEvent } from "@/lib/event_types";
import { getWelcomeMessage } from "@/lib/welcome_message";
import { doLog } from "@/lib/globals";
import { useLocalStorage, localStorageGet, STORAGE_KEYS } from "@/lib/storage";
import { nanoid } from "@/lib/nanoid";

const X_RAY = false;

export default function ChatInterface() {
  // ====================================================================================|
  // MARK: Session Storage
  // NB: session storage is used to persist state across page loads
  const [messages, setMessages] = useLocalStorage(STORAGE_KEYS.messages, []);
  const [inActivity, setInActivity] = useLocalStorage(STORAGE_KEYS.inActivity, false);
  const [targetLangIsRtl, setTargetLangIsRtl] = useLocalStorage(
    STORAGE_KEYS.targetLangIsRtl,
    false
  );
  const [completedAssessment, setCompletedAssessment] = useLocalStorage(
    STORAGE_KEYS.completedAssessmentKey,
    null
  );

  // ====================================================================================|
  // MARK: Page State
  // NB: page state is used to track the state of the page within a single page load
  const [taaliaIsThinking, setTaaliaIsThinking] = useState(false);
  const [taaliaIsScoring, setTaaliaIsScoring] = useState(false);
  const [modalWidget, setModalWidget] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalIsFullScreen, setModalIsFullScreen] = useState(false);

  // ====================================================================================|
  // MARK: Memos
  // NB: memos prevent unnecessary re-renders
  const queryParams = useMemo(() => getQueryParamsFromUrl(), []);
  const strings = useMemo(
    () => ({
      get_app: getL11nString("get_app"),
      scoring_report: getL11nString("scoring_report"),
    }),
    []
  );

  // ====================================================================================|
  // MARK: Refs
  const chatScaffoldRef = useRef(null);

  // ====================================================================================|
  //  MARK: Getters
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const replyOptions = lastMessage?.replyOptions ?? [];
  const lastTaaliaMessage =
    messages.length > 0
      ? messages
          .slice()
          .reverse()
          .find((msg) => msg.senderId.toLowerCase() === "taalia")
      : null;

  // ====================================================================================|
  //  MARK: Page Load

  const isFirstLoad = messages.length === 0;
  if (isFirstLoad) {
    postEvent(TaaliaEvent.chatWidgetLoadStart);
  }

  // NB: useEffect with no params runs once after render!
  useEffect(() => {
    if (doLog()) {
      console.log("Chat Interface Start Render");
      //   const hostName = window.location.hostname;
      //   console.log("Current Host Name:", hostName);
    }

    const isFirstLoad = messages.length === 0;
    if (isFirstLoad) {
      // postLog("MESSAGE_STATE: Initial load - empty messages array");
      if (doLog()) console.log("MESSAGE_STATE: Initial load - empty messages array");

      // Render the welcome message
      const welcomeMessage = getWelcomeMessage();
      addMessages([welcomeMessage]);
    } else {
      if (doLog()) console.log("REFRESH");
      // postLog("REFRESH"); // PUBLISH EVENT - important for bug analysis

      // check if the query params include 'stripe_session_id'
      if (queryParams.stripe_session_id || queryParams.payment_return_success) {
        if (doLog()) {
          console.log(
            "Stripe session ID found in query params. Posting post-payment message."
          );
        }
        postPostPaymentMessage();
        return;
      }

      // If the last message was a 500 error and the previous message was a user message
      const lastIndex = messages.length - 1;
      const previousMessage = messages[lastIndex - 1];
      if (lastMessage?.isError && previousMessage?.senderId === "user") {
        if (doLog()) {
          console.log("MESSAGE_STATE: Error recovery - removing error message", {
            errorMessage: lastMessage,
            previousMessage,
          });
        }
        // postLog("MESSAGE_STATE: Error recovery - removing error message", {
        //   errorMessage: lastMessage,
        //   previousMessage,
        // });

        // remove the error message from the messages array
        setMessages((prevMessages) => prevMessages.slice(0, -1));

        // re-post the last message that the user entered
        postMessage(previousMessage);
      }
      return; // Don't fetch initial message
    }

    // requestAnimationFrame(() => setMounted(true));

    if (isFirstLoad) {
      postEvent(TaaliaEvent.chatWidgetLoadEnd);
    }
  }, []);

  // ====================================================================================|
  // MARK: Post Message

  const postMessage = async (message) => {
    try {
      // Add an id to the message first
      message.id = nanoid();

      // Add the message to the UI immediately
      addMessages([message]);

      // Wait for state update to complete
      setTimeout(scrollToBottom, 0);

      // Set thinking state
      setTaaliaIsThinking(true);

      if (lastMessage?.renderedAt) {
        const timeElapsed = new Date() - lastMessage.renderedAt;
        if (doLog()) console.log(`Time elapsed: ${timeElapsed}ms`);
        message.timeElapsedMS = timeElapsed;
      }

      // // TEMPORARY: log the message being posted
      // const writeObj = {
      //   outgoingMessage: message,
      //   existingMessages: messages,
      // };
      // postLog(
      //   `POST MESSAGE with ixid: ${message.ixid?.messageId ?? "unknown"}`,
      //   writeObj
      // );
      // // END TEMPORARY

      // Check for completed assessment
      if (completedAssessment) {
        const msg1 = "@@@ CANCELLING MESSAGE POST BECAUSE OF COMPLETE ASSESSMENT";
        postLog(msg1);
        if (doLog()) console.log(msg1);
        setTaaliaIsThinking(false);
        return;
      }

      if (doLog()) console.log("POST MSG messages:", messages);

      // Post the message to the server
      const systemResponse = await postChat(message, queryParams, true);

      // Handle the new server messages
      processNewServerMessages(systemResponse);
      setTaaliaIsThinking(false);
    } catch (error) {
      const msg = "Error in postMessage()";
      console.error(msg, error);
      postError(msg, error);
      processNewServerMessages([{ utterance: "Error posting message." }]);
      setTaaliaIsThinking(false);
    }
  };

  // ====================================================================================|
  //  MARK: Rec. Msgs

  const processNewServerMessages = (newServerMessages) => {
    if (doLog()) console.log("processNewServerMessages()", newServerMessages);

    if (newServerMessages.length === 0) {
      console.log("No new server messages. Returning.");
      return;
    }

    // TEMPORARY: log the new server messages
    console.log("RECEIVE MSG(s)", newServerMessages);
    // postLog("RECEIVE MSG(s)", newServerMessages);
    // END TEMPORARY

    // Set last message rendered at
    const lastNewMessage = newServerMessages[newServerMessages.length - 1];
    lastNewMessage.renderedAt = new Date();

    // Store completed assessment
    if (lastNewMessage?.extendedProperties?.assessment) {
      setCompletedAssessment(lastNewMessage.extendedProperties.assessment);
    }

    // In activity
    const msgInActivity = lastNewMessage.inActivity ?? false;
    setInActivity(msgInActivity);

    // Target language directionality
    const targetLangDirectionality = lastNewMessage.targetLangDirectionality;
    if (targetLangDirectionality) {
      const msgTargetLangIsRtl = targetLangDirectionality === "rtl";
      setTargetLangIsRtl(msgTargetLangIsRtl);
    }

    // Handle input type
    if (lastNewMessage.inputType) {
      switch (lastNewMessage.inputType) {
        case "checkout_session":
          window.location.href = lastNewMessage.checkoutSessionUrl;
          break;

        case "activity_picker": {
          setModalWidget(
            <ActivityPicker
              utterance={lastNewMessage.utterance}
              activityTypes={lastNewMessage.replyOptions}
              onActivitySelect={handleButtonClick}
              closeModal={closeModal}
            />
          );
          openModal();
          // DO NOT ADD TO MESSAGES ARRAY
          newServerMessages.pop();
          break;
        }

        case "sku_options":
          setModalWidget(
            <SkuOptions message={lastNewMessage} onSkuSelected={handleButtonClick} />
          );
          setModalIsFullScreen(true);
          openModal();
          // DO NOT ADD TO MESSAGES ARRAY
          newServerMessages.pop();
          break;

        default:
          break;
      }
    }

    // Add the new messages to the messages array
    addMessages(newServerMessages);

    // End of activity check / start polling for score
    if (msgInActivity) {
      if (lastNewMessage.isLast) {
        if (doLog()) console.log("Last message in activity. Starting score polling.");
        setTaaliaIsThinking(true);
        setTaaliaIsScoring(true);
      }
    }

    // Wait for state update to complete
    setTimeout(scrollToBottom, 0);
  };

  // ====================================================================================|
  // MARK: addMessages

  const addMessages = (newMessages) => {
    if (newMessages.length === 0) {
      if (doLog()) console.log("No new messages to add. Returning.");
      return;
    }

    // TEMP
    console.log("MESSAGE_STATE: Before adding messages", {
      currentMessageCount: messages.length,
      newMessages,
    });
    // postLog("MESSAGE_STATE: Before adding messages", {
    //   currentMessageCount: messages.length,
    //   newMessages,
    // });
    // END TEMP

    // Add the new messages to the existing messages in session storage
    // console.warn("**** CALLING setMessages A ****");
    setMessages((prevMessages) => {
      // console.warn("PREV MESSAGES", prevMessages);

      for (const newMessage of newMessages) {
        newMessage.addedAt = new Date();
      }

      const combinedMessages = prevMessages.concat(newMessages);

      // TEMPORARY: log the new messages
      const writeObj = {
        existingMessages: prevMessages,
        newMessages,
        combinedMessages,
      };
      console.log("ADDED MESSAGES", writeObj);
      // postLog("ADDED MESSAGES", writeObj);
      // END TEMPORARY

      const sortedMessages = combinedMessages.sort((a, b) => {
        const timeA = a.addedAt ? new Date(a.addedAt).getTime() : 0;
        const timeB = b.addedAt ? new Date(b.addedAt).getTime() : 0;
        return timeA - timeB;
      });

      // TEMP
      console.log("MESSAGE_STATE: After adding messages", {
        previousMessageCount: prevMessages.length,
        newMessageCount: newMessages.length,
        finalMessageCount: sortedMessages.length,
      });
      // postLog("MESSAGE_STATE: After adding messages", {
      //   previousMessageCount: prevMessages.length,
      //   newMessageCount: newMessages.length,
      //   finalMessageCount: sortedMessages.length,
      // });
      // END TEMP

      return sortedMessages;
    });
  };

  // ====================================================================================|
  //  MARK: Score Polling

  useEffect(() => {
    if (taaliaIsScoring) {
      (async () => {
        const scoreMessage = await pollForScore();
        processNewServerMessages([scoreMessage]);
        setTaaliaIsThinking(false);
        setTaaliaIsScoring(false);
      })();
    }
  }, [taaliaIsScoring]);

  // ====================================================================================|
  // MARK: Post Post Payment Message

  const postPostPaymentMessage = async () => {
    // The user is returning from a payment, we need to send a message to the server to let it know
    const success = queryParams.payment_return_success === "true";
    let stripeSessionId;
    if (success) {
      stripeSessionId = queryParams.stripe_session_id;
    }

    const lastMessageRenderedId = lastMessage?.id ?? null;
    let message = {
      senderId: "user",
      lastMessageRenderedId,
      ixid: { messageId: "payment" },
      timestamp: new Date(),
      success,
      stripeSessionId,
    };

    // Clear the query params
    if (doLog()) console.log("Clearing the query params");
    window.history.replaceState({}, document.title, window.location.pathname);

    // Post the message to the server
    postMessage(message);
  };

  // ====================================================================================|
  //  MARK: Text Input

  const handleTextInput = (utterance, userTypingMS = 0) => {
    try {
      utterance = utterance.trim();
      if (utterance.length === 0) {
        if (doLog()) console.log("handleTextInput: Empty utterance. Returning.");
        return;
      }

      const userInputIsRtl = inActivity ? targetLangIsRtl : uiLangIsRtl();
      const directionality = userInputIsRtl ? "rtl" : "ltr";
      if (doLog()) console.log("handleTextInput directionality:", directionality);

      const message = {
        senderId: "user",
        utterance,
        ixid: lastMessage?.ixid,
        directionality,
        userTypingMS,
      };
      postMessage(message);

      // Update the last Taalia message's userResponsesToGo
      if (lastTaaliaMessage?.userResponsesToGo !== undefined) {
        setMessages((prevMessages) => {
          const lastTaaliaIndex = [...prevMessages]
            .reverse()
            .findIndex((msg) => msg.senderId.toLowerCase() === "taalia");
          if (lastTaaliaIndex === -1) return prevMessages;

          const forwardIndex = prevMessages.length - 1 - lastTaaliaIndex;
          return prevMessages.map((msg, index) => {
            if (index === forwardIndex) {
              return {
                ...msg,
                userResponsesToGo: Math.max(0, msg.userResponsesToGo - 1),
              };
            }
            return msg;
          });
        });
      }
    } catch (error) {
      const msg = "Error in handleTextInput()";
      console.error(msg, error);
      postError(msg, error);
      processNewServerMessages({
        utterance: "Error generating response from the server.",
      });
    }
  };

  // ====================================================================================|
  // MARK: Email/Tel Input

  const handleEmailPhoneInput = (inputObj) => {
    try {
      if (doLog()) console.log(`handleEmailPhoneInput: ${JSON.stringify(inputObj)}`);
      const utterance = inputObj.value;
      const country = inputObj.country;
      const turnstileToken = inputObj.turnstileToken;
      const isWhatsApp = inputObj.isWhatsApp;
      const message = {
        senderId: "user",
        utterance,
        country,
        turnstileToken,
        isWhatsApp,
        ixid: lastMessage?.ixid,
      };
      postMessage(message);
    } catch (error) {
      const msg = "Error in handleEmailPhoneInput()";
      console.error(msg, error);
      postError(msg, error);
      processNewServerMessages({
        utterance: "Error generating response from the server.",
      });
    }
  };

  // ====================================================================================|
  // MARK: Otp Input

  const handleOtpInput = (inputObj) => {
    try {
      if (doLog()) console.log(`handleOtpInput: ${inputObj}`);
      const message = {
        senderId: "user",
        utterance: inputObj,
        ixid: lastMessage?.ixid,
      };
      postMessage(message);
    } catch (error) {
      const msg = "Error in handleOtpInput()";
      console.error(msg, error);
      postError(msg, error);
      processNewServerMessages({
        utterance: "Error generating response from the server.",
      });
    }
  };

  // ====================================================================================|
  // MARK: Button Click

  const handleButtonClick = async (replyOption) => {
    if (doLog()) console.log(`handleButtonClick: ${JSON.stringify(replyOption)}`);

    // In some cases, the modal is open when the button is clicked.
    closeModal();

    // Get the selection intent
    const selectionIntent = replyOption.selectionIntent;

    //
    if (selectionIntent) {
      // PUBLISH EVENT - important for analytics
      await postEvent(TaaliaEvent.selectionIntent, {
        selectionIntent,
        ixid: lastMessage?.ixid,
      });

      // HANDLE LOCAL SELECTION INTENT
      if (selectionIntent.startsWith("local_")) {
        handleLocalSelectionIntent(selectionIntent);
        return;
      }
    }

    // Clean up the utterance
    const utterance = replyOption.title.replace(/<\/?p>/g, "");

    // Build the message
    const message = {
      senderId: "user",
      utterance,
      selectionIntent,
      ixid: lastMessage?.ixid,
    };

    // Post the message
    await postMessage(message);
  };

  // ====================================================================================|
  // MARK: Selection Intent

  const handleLocalSelectionIntent = (selectionIntent) => {
    if (doLog()) console.log(`handleLocalSelectionIntent: ${selectionIntent}`);
    switch (selectionIntent) {
      case "local_view_report": {
        setModalWidget(
          <ActivityReportView assessment={completedAssessment} closeModal={closeModal} />
        );
        setModalIsFullScreen(true);
        openModal();
        break;
      }
      default:
        if (doLog()) console.log(`Unhandled local selection intent: ${selectionIntent}`);
        break;
    }
  };

  // ====================================================================================|
  // MARK: App DL Click

  const handleAppDownloadClick = () => {
    setModalWidget(<AppDownloadModal />);
    openModal();
  };

  // ====================================================================================|
  //  MARK: Text Input Focus

  const onTextInputFocus = () => {
    if (doLog()) console.log("onTextInputFocus called");

    // First notify about viewport change
    sendExpectViewportChange();

    // Wait a short moment for viewport to settle before scrolling
    requestAnimationFrame(() => {
      if (doLog())
        console.log("onTextInputFocus: triggering scroll after viewport change");
      scrollToBottom();
    });
  };

  const onTextInputBlur = () => {
    if (doLog()) console.log("onTextInputBlur");
    sendExpectViewportChange();
  };

  const sendExpectViewportChange = () => {
    if (doLog()) console.log("sendExpectViewportChange");
    if (!chatScaffoldRef.current) {
      if (doLog()) console.log("Missing chatScaffoldRef");
      return;
    }
    if (doLog()) console.log("sendExpectViewportChange - calling expectViewportChange");
    chatScaffoldRef.current?.expectViewportChange?.();
  };

  // ====================================================================================|
  //  MARK: Viewport

  const onViewportChange = () => {
    if (doLog()) console.log("onViewportChange");
    scrollToBottom();
  };

  // ====================================================================================|
  //  MARK: Scroll to Bottom

  const scrollToBottom = () => {
    if (doLog()) console.log("scrollToBottom called");
    chatScaffoldRef.current?.scrollToBottom?.();
  };

  // ====================================================================================|
  //  MARK: Modals

  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    // Don't allow closing if current widget is AssessmentPicker
    if (modalWidget?.type === ActivityPicker) {
      return;
    }
    setModalIsOpen(false);
    setModalIsFullScreen(false);
    setModalWidget(null);
  };

  const modalContent = () => {
    return modalIsOpen && modalWidget ? modalWidget : null;
  };

  const showPrivacyPolicy = () => {
    setModalWidget(<PrivacyPolicy showCloseButton={true} onClose={closeModal} />);
    openModal();
  };

  // ====================================================================================|
  //  MARK: Completed Assessment Modal

  useEffect(() => {
    if (completedAssessment) {
      const reportViewed = localStorageGet(STORAGE_KEYS.activityReportViewedKey);
      if (reportViewed) {
        setModalWidget(<ContinueInAppModal assessment={completedAssessment} />);
      } else {
        setModalWidget(<ActivityReportView assessment={completedAssessment} />);
      }
      setModalIsFullScreen(true);
      openModal();
    }
  }, [completedAssessment]);

  // ====================================================================================|
  //  MARK: Activity Prog

  const renderActivityProgressBar = () => {
    if (messages.length === 0) return null;
    const total = lastTaaliaMessage?.totalResponses;
    if (total === undefined || total < 1) return null;
    const toGo = lastTaaliaMessage?.userResponsesToGo;
    if (toGo === undefined) return null;
    const current = total - toGo;
    if (doLog())
      console.log(
        "renderActivityProgressBar - total:",
        total,
        "toGo:",
        toGo,
        "current:",
        current
      );
    return <ActivityProgressBar total={total} current={current} />;
  };

  // ====================================================================================|
  //  MARK: Footer Setup

  const getFooterContentType = () => {
    if (messages.length === 0) {
      return FOOTER_TYPE.none;
    }
    switch (lastMessage.inputType) {
      case "email":
        return FOOTER_TYPE.emailInput;
      case "app_store_tiles":
        return FOOTER_TYPE.appStoreTiles;
      case "sku_options":
        return FOOTER_TYPE.skuTiles;
      case "single_select":
      case "multi_select":
        return FOOTER_TYPE.buttonRow;
      case "text":
        return FOOTER_TYPE.messageInput;
      case "otp":
        return FOOTER_TYPE.otpInput;
      default:
        return FOOTER_TYPE.none;
    }
  };

  const buildFooterContent = () => {
    // Thinking / Scoring Footer
    if (taaliaIsThinking || taaliaIsScoring) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            paddingLeft: "16px",
            gap: "12px",
          }}
        >
          {taaliaIsScoring && <p>{strings.scoring_report}</p>}
          <ThinkingAnimation />
        </div>
      );
    }

    // Message Dependent Footer
    const footerContentType = getFooterContentType();

    switch (footerContentType) {
      case FOOTER_TYPE.buttonRow:
        return (
          <ButtonRow replyOptions={replyOptions} onButtonClick={handleButtonClick} />
        );
      case FOOTER_TYPE.emailInput:
        return (
          <EmailPhoneInputWrapper
            onSubmit={handleEmailPhoneInput}
            onFocus={onTextInputFocus}
            onBlur={onTextInputBlur}
            showPrivacyPolicy={showPrivacyPolicy}
          />
        );
      case FOOTER_TYPE.appStoreTiles:
        return (
          <AppStoreTiles replyOptions={replyOptions} onButtonClick={handleButtonClick} />
        );
      case FOOTER_TYPE.messageInput:
        // NB: return null because the message input is rendered in the main content area
        return null;
      case FOOTER_TYPE.otpInput:
        return (
          <OtpInput
            onSubmit={handleOtpInput}
            onFocus={onTextInputFocus}
            onBlur={onTextInputBlur}
            placeholder={lastMessage?.xp?.input_placeholder}
          />
        );
      default:
        if (doLog()) console.warn(`Unknown footer content type: ${footerContentType}`);
        return null;
    }
  };

  // ====================================================================================|
  // MARK: Render Header

  const renderHeader = () => {
    return (
      <div
        className="z-10 pt-2 pb-2 border"
        style={{
          borderColor: X_RAY && isDevHost ? "red" : "white",
          backgroundColor: "white",
          // transition: `all ${TRANSITION_DURATION}ms`,
        }}
      >
        <div className="flex w-full items-center relative">
          <div className="absolute pl-0 z-10">
            <HamburgerMenu />
          </div>
          <div className="w-full flex justify-center">
            <Logo onClick={handleAppDownloadClick} />
          </div>
          <div className="absolute right-2 z-10">
            <a
              href="#"
              className="block pr-0 py-2 text-xs text-gray-700 hover:bg-gray-100"
              role="menuitem"
              onClick={handleAppDownloadClick}
            >
              {strings.get_app}
            </a>
          </div>
        </div>
      </div>
    );
  };

  // ====================================================================================|
  // MARK: Render Footer

  const renderFooter = () => {
    return (
      <div className="flex flex-col gap-0">
        {renderActivityProgressBar()}
        <MessageInput
          onSubmit={handleTextInput}
          onFocus={onTextInputFocus}
          onBlur={onTextInputBlur}
          isVisible={
            getFooterContentType() === FOOTER_TYPE.messageInput && !taaliaIsThinking
          }
          placeholder={lastMessage?.xp?.input_placeholder}
          emphasize={lastMessage?.emphasize}
        />
        {buildFooterContent()}
      </div>
    );
  };

  // ====================================================================================|
  //  MARK: Final Render

  return (
    <>
      <div className="max-w-3xl mx-auto w-full">
        <ChatScaffold
          ref={chatScaffoldRef}
          header={renderHeader()}
          body={<MessageList messages={messages} />}
          footer={renderFooter()}
          onViewportChange={onViewportChange}
        />
      </div>
      {modalIsOpen && (
        <div
          className="modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]"
          onClick={closeModal}
        >
          <div
            className="modal-content bg-white text-black px-0 py-0 text-lg rounded m-0 flex flex-col"
            style={{
              minHeight: modalIsFullScreen ? "100vh" : "20vh",
              width: modalIsFullScreen ? "100vw" : "90vw",
              maxWidth: modalIsFullScreen ? "100vw" : "260px",
              maxHeight: modalIsFullScreen ? "100vh" : "90vh",
              borderRadius: modalIsFullScreen ? "0" : "0.5rem",
              overflowY: "auto",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "stretch",
              paddingTop: modalWidget?.type === ActivityReportView ? "16px" : "0",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              {modalContent()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
